import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { filesAPIService } from '~/api/files';
import { qrcodeAPIService } from '~/api/qrcode';
import DesktopBuilderPreviewV2 from '~/components/builder/DesktopBuilderPreviewV2';
import { DownloadQrStep } from '~/components/builder/DownloadQrStep/DownloadQrStep';
import QRCodeCornerStyle from '~/components/QRCodeDesign/QRCodeCornerStyle/QRCodeCornerStyle';
import QRCodeCorrectionLevel from '~/components/QRCodeDesign/QRCodeCorrectionLevel';
import QRCodeFrame from '~/components/QRCodeDesign/QRCodeFrame';
import QRCodeLogo from '~/components/QRCodeDesign/QRCodeLogo/QRCodeLogo';
import QRCodePattern from '~/components/QRCodeDesign/QRCodePattern/QRCodePattern';
import { BASIC_GRADIENT_STYLES, DESKTOP_MIN_WIDTH, PREVIEW_BAR_VIEW_TYPE } from '~/constants/common';
import {
  QR_CODE_ID_PARAM_NAME,
  QR_CODE_TEMPLATE_PARAM_NAME,
  QRCODE_DYNAMIC_MICROSITE_TYPE,
  QRCODE_STATUS,
} from '~/constants/qr-code';
import { qrCodeKeys } from '~/constants/queryKeys';
import { PATH } from '~/constants/route-path';
import useBuilderBottomNavigationElement from '~/hooks/useBuilderBottomNavigationElement';
import useQrCodeData from '~/hooks/useQrCodeData';
import useQrCodeStylesData from '~/hooks/useQrCodeStylesData';
import NavigationBottom from '~/pages/CreateQR/components/NavigationBottom';
import useBuilderStore from '~/store/useBuilderStore';
import useCouponTemplateStore from '~/store/useCouponTemplateStore';
import { QrCodeLogoState } from '~/types/builder';
import {
  ChangeGradientParams,
  ChangeIsGradientParams,
  DynamicQrCodeData,
  QrCodeCorrectionLevel,
  QrCodeFrameItem,
  QrCodeStyle,
  StaticQrCodeData,
} from '~/types/qrCode';
import { getFileFromLocalSource } from '~/utils/common';
import { logger } from '~/utils/logger';

const DEFAULT_COLOR = '#000000';

export default function CreateQrDesignQRStepV2() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const [selectedLogo, setSelectedLogo] = useState<QrCodeLogoState>({ name: 'empty', file: null });
  const [isCompleteLoading, setIsCompleteLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const { bottomNavigationElement } = useBuilderBottomNavigationElement();
  const styles = useStyles();
  const { setActiveStep, setPreviewViewType } = useBuilderStore();
  const { resetCouponTemplateState } = useCouponTemplateStore();

  const qrCodeId = searchParams.get(QR_CODE_ID_PARAM_NAME);
  const templateId = searchParams.get(QR_CODE_TEMPLATE_PARAM_NAME) || null;

  const { data: qrCodeStyles } = useQrCodeStylesData();
  const { data: qrCodeData } = useQrCodeData<DynamicQrCodeData | StaticQrCodeData>({ qrCodeId });

  const qrCodeDataStylesRef = useRef(qrCodeData?.style);
  const {
    mutate: updateQrCodeMutate,
    isPending: isPendingUpdateQrCode,
    mutateAsync: updateQrCodeMutateAsync,
  } = useMutation({
    mutationFn: (body) => qrcodeAPIService.updateQrCode(body, qrCodeId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qrCodeKeys.qrCodeData });
    },
  });

  const { mutate: uploadImage } = useMutation({
    mutationFn: filesAPIService.uploadImage,
    onSuccess: (response) => {
      updateQrCodeMutate({
        embedded_logo_id: response.data,
      });
    },
  });

  useEffect(() => {
    if (qrCodeData?.style) {
      qrCodeDataStylesRef.current = qrCodeData.style;
    }
  }, [qrCodeData]);

  const handleChangeFrame = (newFrame: QrCodeFrameItem) => {
    const newStyle = {
      ...qrCodeDataStylesRef.current,
      frame: {
        ...qrCodeDataStylesRef.current?.frame,
        style: newFrame.id,
        phrase_color: newFrame.defaultTextColor,
      },
    };

    qrCodeDataStylesRef.current = newStyle;

    updateQrCodeMutate({
      style: newStyle,
    });
  };

  const handleChangeShape = (newShape: string) => {
    const newStyle = {
      ...qrCodeDataStylesRef.current,
      shape: {
        ...qrCodeDataStylesRef.current?.shape,
        style: newShape,
      },
    };

    qrCodeDataStylesRef.current = newStyle;

    updateQrCodeMutate({
      style: newStyle,
    });
  };

  const handleChangeLevel = (newLevel: QrCodeCorrectionLevel) => {
    const newStyle = {
      ...qrCodeDataStylesRef.current,
      error_correction_level: newLevel,
    };

    qrCodeDataStylesRef.current = newStyle;

    updateQrCodeMutate({
      style: newStyle,
    });
  };

  const handleChangeLogo = async (newLogo: QrCodeLogoState) => {
    if (newLogo.name === 'empty') {
      void handleDeleteUploadedLogo();
    } else {
      setSelectedLogo(newLogo);
      const fileURL = await getFileFromLocalSource(window.URL.createObjectURL(newLogo.file));
      uploadImage(fileURL);
    }
  };

  const handleDeleteUploadedLogo = async () => {
    setSelectedLogo({ name: 'empty', file: null });

    updateQrCodeMutate({
      embedded_logo_id: null,
    });
  };

  const handleUpdateQrStyles = (newStyles: QrCodeStyle) => {
    qrCodeDataStylesRef.current = newStyles;
    updateQrCodeMutate({
      style: newStyles,
    });
  };

  const handleChangeGradient = ({ field, value, parentField, parentDbField }: ChangeGradientParams) => {
    if (qrCodeData?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        [parentDbField]: {
          ...qrCodeDataStylesRef.current[parentField],
          color: {
            ...qrCodeDataStylesRef.current[parentField].color,
            [field]: value,
          },
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      updateQrCodeMutate({ style: newStyle });
    }
  };

  const handleChangeIsGradient = ({ field, checked, defaultValue, dbField }: ChangeIsGradientParams) => {
    if (qrCodeData?.style) {
      if (checked) {
        const newStyle = {
          ...qrCodeDataStylesRef.current,
          [dbField]: {
            ...qrCodeDataStylesRef.current[field],
            color: {
              ...BASIC_GRADIENT_STYLES,
              color: defaultValue ?? DEFAULT_COLOR,
            },
          },
        };

        qrCodeDataStylesRef.current = newStyle;

        updateQrCodeMutate({ style: newStyle });
      } else {
        const newStyle = {
          ...qrCodeDataStylesRef.current,
          [dbField]: {
            ...qrCodeDataStylesRef.current[field],
            color: DEFAULT_COLOR,
          },
        };

        qrCodeDataStylesRef.current = newStyle;

        updateQrCodeMutate({ style: newStyle });
      }
    }
  };

  const handleChangeBorderStyle = (newBorderStyle: number) => {
    if (qrCodeData?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        corner_border: {
          ...qrCodeDataStylesRef.current?.cornerBorder,
          style: newBorderStyle,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      updateQrCodeMutate({ style: newStyle });
    }
  };

  const handleChangeBorderColor = (newBorderColor: string) => {
    if (qrCodeData?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        corner_border: {
          ...qrCodeDataStylesRef.current?.cornerBorder,
          color: newBorderColor,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      updateQrCodeMutate({ style: newStyle });
    }
  };

  const handleChangeCenterColor = (newCenterColor: string) => {
    if (qrCodeData?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        corner_center: {
          ...qrCodeDataStylesRef.current?.cornerCenter,
          color: newCenterColor,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      updateQrCodeMutate({ style: newStyle });
    }
  };

  const handleSwitchColors = () => {
    if (qrCodeData?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        corner_center: {
          ...qrCodeDataStylesRef.current?.cornerCenter,
          color: qrCodeDataStylesRef.current?.cornerBorder?.color,
        },
        corner_border: {
          ...qrCodeDataStylesRef.current?.cornerBorder,
          color: qrCodeDataStylesRef.current?.cornerCenter?.color,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      updateQrCodeMutate({ style: newStyle });
    }
  };

  const handleChangeColor = (newColor: string) => {
    if (qrCodeData?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        shape: {
          ...qrCodeDataStylesRef.current?.shape,
          color: newColor,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      updateQrCodeMutate({ style: newStyle });
    }
  };

  const handleChangeCenterStyle = (newCenterStyle: number) => {
    if (qrCodeData?.style) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        corner_center: {
          ...qrCodeDataStylesRef.current?.cornerCenter,
          style: newCenterStyle,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      updateQrCodeMutate({ style: newStyle });
    }
  };

  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
    // // TODO need to fix properly, in CreateQr there is useEffect with setSearchParams(''), it's affect navigation to my qrs page (ticket - 1 step is shown after creation QR)
    setTimeout(() => {
      setActiveStep(0);
    }, 500);
    setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.PHONE);
    navigate(PATH.QR.MAIN);

    if ('micrositeType' in qrCodeData && qrCodeData.micrositeType === QRCODE_DYNAMIC_MICROSITE_TYPE.COUPON) {
      resetCouponTemplateState();
    }
  };

  const handleComplete = async () => {
    try {
      setIsCompleteLoading(true);
      await updateQrCodeMutateAsync({ status: QRCODE_STATUS.DONE });
      setActiveStep(3);
      setShowDownloadModal(true);
      window.dataLayer?.push({
        event: 'create_qr_code_completed',
        qr_code_id: qrCodeId,
        qr_code_type: templateId,
      });
    } catch (error) {
      logger.log('handleComplete', error);
    } finally {
      setIsCompleteLoading(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.left}>
        <QRCodeFrame
          frames={qrCodeStyles?.frames}
          defaultSelectedFrame={qrCodeData?.style?.frame?.style}
          onSelectFrame={handleChangeFrame}
          qrStyles={qrCodeData?.style}
          onUpdateQrStyles={handleUpdateQrStyles}
        />
        <QRCodePattern
          defaultSelectedShape={qrCodeData?.style?.shape?.style}
          onSelectShape={handleChangeShape}
          isAdvanced
          onChangeColor={handleChangeColor}
          onChangeGradient={(params) =>
            handleChangeGradient({ ...params, parentField: 'shape', parentDbField: 'shape' })
          }
          onChangeIsGradient={(e) =>
            handleChangeIsGradient({
              field: 'shape',
              dbField: 'shape',
              checked: e.target.checked,
              defaultValue: qrCodeData?.style?.shape?.color,
            })
          }
          qrCodeStyles={qrCodeData?.style}
        />
        <QRCodeCornerStyle
          onChangeBorderStyle={handleChangeBorderStyle}
          onChangeCenterStyle={handleChangeCenterStyle}
          onSwitchBorderAndCenterColors={handleSwitchColors}
          onChangeBorderColor={handleChangeBorderColor}
          onChangeCenterColor={handleChangeCenterColor}
          onChangeBorderGradient={(params) =>
            handleChangeGradient({ ...params, parentDbField: 'corner_border', parentField: 'cornerBorder' })
          }
          onChangeCenterGradient={(params) =>
            handleChangeGradient({ ...params, parentDbField: 'corner_center', parentField: 'cornerCenter' })
          }
          onChangeIsBorderGradient={(e) =>
            handleChangeIsGradient({
              dbField: 'corner_border',
              field: 'cornerBorder',
              checked: e.target.checked,
              defaultValue: qrCodeData?.style?.cornerBorder?.color,
            })
          }
          onChangeIsCenterGradient={(e) =>
            handleChangeIsGradient({
              dbField: 'corner_center',
              field: 'cornerCenter',
              checked: e.target.checked,
              defaultValue: qrCodeData?.style?.cornerCenter?.color,
            })
          }
          qrCodeStyles={qrCodeData?.style}
        />
        <QRCodeCorrectionLevel
          levels={qrCodeStyles?.errorCorrectionLevels}
          onSelectLevel={handleChangeLevel}
          defaultSelectedLevel={qrCodeData?.style?.errorCorrectionLevel}
        />
        <QRCodeLogo
          selectedLogo={selectedLogo}
          onSelectLogo={handleChangeLogo}
          onDeleteUploadedLogo={handleDeleteUploadedLogo}
        />
      </Box>
      {isDesktop && <DesktopBuilderPreviewV2 isUpdatingQrCodePending={isPendingUpdateQrCode && !isCompleteLoading} />}
      {isDesktop &&
        bottomNavigationElement &&
        createPortal(
          <NavigationBottom isValid handleNext={handleComplete} isNextProcessing={isCompleteLoading} />,
          bottomNavigationElement,
        )}
      {showDownloadModal && (
        <DownloadQrStep
          onClose={handleCloseDownloadModal}
          open={showDownloadModal}
          id={qrCodeData.id}
          imageName={qrCodeData.name}
        />
      )}
    </Box>
  );
}

function useStyles() {
  return {
    container: css`
      display: flex;
      gap: 24px;
    `,
    left: css`
      flex: 1;
      max-width: calc(100dvw - 32px);

      @media (min-width: ${DESKTOP_MIN_WIDTH}px) {
        max-width: unset;
      }
    `,
  };
}
